import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { EnTetePage } from '../../commun/EnTetePage';
interface Controle {
  id: string;
  organisation: string;
  fictive: boolean;
  titre: string;
  reference_source: string;
  constat: string;
  recommandation: string;
  statut: string;
  propose_par: string;
  motif_controle: string | null;
}
export function PageRegulation() {
  const { contexte } = useSession();
  const client = useQueryClient();
  const [erreur, setErreur] = useState('');
  const [occupe, setOccupe] = useState(false);
  const autorise =
    contexte?.appartenance_active?.institution === 'regulateur' &&
    peut(contexte, 'regulation', 'voir');
  const q = useQuery({
    queryKey: ['controles-regulation', contexte?.appartenance_active?.id],
    enabled: autorise,
    queryFn: () =>
      requete<{
        elements: Controle[];
        tronque: boolean;
        peut_proposer: boolean;
        peut_valider: boolean;
      }>('/controles-regulation'),
  });
  const orgs = useQuery({
    queryKey: ['controles-regulation', 'organisations', contexte?.appartenance_active?.id],
    enabled: autorise && !!q.data?.peut_proposer,
    queryFn: () =>
      requete<{ elements: { id: string; nom: string; fictive: boolean }[]; tronque: boolean }>(
        '/controles-regulation/organisations',
      ),
  });
  async function envoyer(path: string, corps: unknown) {
    setErreur('');
    setOccupe(true);
    try {
      await requete(path, { methode: 'POST', corps });
      await client.invalidateQueries({ queryKey: ['controles-regulation'] });
      return true;
    } catch (e) {
      setErreur(messageErreur(e));
      return false;
    } finally {
      setOccupe(false);
    }
  }
  if (!autorise) return <p role="alert">Mandat de régulation requis.</p>;
  return (
    <section>
      <EnTetePage titre="Contrôles du régulateur" />
      <p>
        Registre interne des observations documentées, dans votre périmètre autorisé. Chaque
        proposition est conservée et doit être contrôlée par un autre compte habilité. La validation
        ne constitue ni une sanction, ni une notification officielle à l’exploitant. Aucun dossier
        individuel d’usager n’est ouvert par ce mandat.
      </p>
      {(erreur || q.error || orgs.error) && (
        <p role="alert">{erreur || messageErreur(q.error ?? orgs.error)}</p>
      )}
      {q.isPending && <p role="status">Chargement des contrôles…</p>}
      {q.data?.peut_proposer && (
        <details>
          <summary>Proposer un contrôle documenté</summary>
          <form
            style={{ display: 'grid', gap: 12, maxWidth: 760 }}
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const d = new FormData(form);
              if (await envoyer('/controles-regulation', Object.fromEntries(d))) form.reset();
            }}
          >
            <label>
              Exploitant concerné{' '}
              <select name="organisation_id" required defaultValue="">
                <option value="">Choisir dans votre périmètre</option>
                {orgs.data?.elements.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.nom}
                    {o.fictive ? ' (démonstration)' : ''}
                  </option>
                ))}
              </select>
            </label>
            {orgs.data?.tronque && (
              <p role="alert">Seuls les 500 premiers exploitants sont proposés.</p>
            )}
            <label>
              Titre du contrôle <input name="titre" required minLength={5} maxLength={200} />
            </label>
            <label>
              Référence de la source examinée{' '}
              <input name="reference_source" required minLength={3} maxLength={200} />
            </label>
            <label>
              Constat documenté{' '}
              <textarea name="constat" required minLength={20} maxLength={4000} rows={4} />
            </label>
            <label>
              Recommandation proposée{' '}
              <textarea name="recommandation" required minLength={20} maxLength={4000} rows={4} />
            </label>
            <p>
              Vérifiez les informations avant envoi : cette proposition ne peut pas être réécrite.
              En cas de rejet, une nouvelle proposition doit être enregistrée.
            </p>
            <button disabled={occupe || orgs.isPending}>Soumettre le contrôle</button>
          </form>
        </details>
      )}
      {q.data?.tronque && (
        <p role="alert">Seuls les 500 contrôles les plus récents sont affichés.</p>
      )}
      {q.data && !q.data.elements.length && <p>Aucun contrôle dans votre périmètre.</p>}
      {q.data?.elements.map((r) => (
        <article key={r.id} style={{ marginBlock: 20 }}>
          <h2>{r.titre}</h2>
          <p>
            {r.organisation}
            {r.fictive ? ' — démonstration' : ''}
          </p>
          <p>
            {
              (
                {
                  PROPOSED: 'À contrôler',
                  VALIDATED: 'Contrôle validé',
                  REJECTED: 'Proposition rejetée',
                } as Record<string, string>
              )[r.statut]
            }
          </p>
          <p>Source : {r.reference_source}</p>
          <h3>Constat</h3>
          <p>{r.constat}</p>
          <h3>Recommandation</h3>
          <p>{r.recommandation}</p>
          {r.motif_controle && <p>Motif du contrôle : {r.motif_controle}</p>}
          {q.data!.peut_valider &&
            r.statut === 'PROPOSED' &&
            r.propose_par !== contexte?.compte.id && (
              <form
                style={{ display: 'grid', gap: 12, maxWidth: 760 }}
                onSubmit={(e) => {
                  e.preventDefault();
                  void envoyer(
                    `/controles-regulation/${r.id}/decision`,
                    Object.fromEntries(new FormData(e.currentTarget)),
                  );
                }}
              >
                <label>
                  Décision sur le contrôle{' '}
                  <select name="decision">
                    <option value="valider">Valider le contrôle</option>
                    <option value="rejeter">Rejeter la proposition</option>
                  </select>
                </label>
                <label>
                  Motivation de la décision{' '}
                  <textarea name="motif" required minLength={20} maxLength={2000} rows={3} />
                </label>
                <button disabled={occupe}>Enregistrer la décision</button>
              </form>
            )}
        </article>
      ))}
    </section>
  );
}
